import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FilesTab } from '@/sections/files/tabs/FilesTab';
import { LOATab } from '@/sections/files/tabs/LOATab';
import { ReportsTab } from '@/sections/files/tabs/ReportsTab';

const TABS = [
  { value: 'reports', label: 'Reports' },
  { value: 'loa', label: 'Letter of Attestation' },
  { value: 'files', label: 'Uploads' },
] as const;

type TabValue = (typeof TABS)[number]['value'];

const VALID_TABS = TABS.map(t => t.value);

const DEFAULT_TAB: TabValue = 'reports';

const Files: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth — derive the selected tab from `?tab=`,
  // falling back to the default when absent or invalid (mirrors Insights.tsx).
  const selectedTab = useMemo<TabValue>(() => {
    const urlTab = searchParams.get('tab');
    return VALID_TABS.includes(urlTab as TabValue)
      ? (urlTab as TabValue)
      : DEFAULT_TAB;
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <AppRouteBody title="Documents" tablePadding={false}>
      <div className="flex h-full flex-col overflow-hidden p-6">
        <Tabs
          value={selectedTab}
          onValueChange={handleTabChange}
          className="flex h-full flex-col overflow-hidden"
        >
          <TabsList variant="boxed">
            {TABS.map(tab => (
              <TabsTrigger key={tab.value} value={tab.value} variant="boxed">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="reports" className="h-full overflow-hidden">
            <ReportsTab />
          </TabsContent>
          <TabsContent value="loa" className="h-full overflow-hidden">
            <LOATab />
          </TabsContent>
          <TabsContent value="files" className="h-full overflow-hidden">
            <FilesTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppRouteBody>
  );
};

export default Files;
